import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';
import { AuthContext } from 'contexts';
import { useHistory, useParams } from 'react-router-dom';
import { Color } from '@bb/elements-web';
import {
  CanManageBillingProfileType,
  SubscriptionsPageProvider,
} from './SubscriptionsPageContext';
import PaymentCard from './components/PaymentCard';
import ContactDetailsCard from './components/ContactDetailsCard';
import AllSubscriptionsCard from './components/AllSubscriptionsCard';
import DocumentsCard from './components/DocumentsCard';
import InvoicesCard from './components/InvoicesCard';
import FormContext from '../UserViewPage/FormContext';
import BillingInvoicesPageContextProvider from '../BillingInvoicesPage/BillingInvoicesPageContext';

/** Whether subscriptions are being managed for an individual user or
 * for an entire brokerage. Brokerage admins can go to both types of pages
 * depending on what URL the access it from. */
export type ViewingLevel = 'user' | 'org';

const SubscriptionsPaymentsPageContent: React.FC = () => {
  // The viewing level must be sent in via the URL params, otherwise default to user.
  const { viewingLevel = 'user' } = useParams<{
    viewingLevel: ViewingLevel;
  }>();
  const history = useHistory();
  const { auth } = useContext(AuthContext);
  const { userId } = useContext(FormContext);

  const isAssumedUser = useMemo(
    () =>
      auth.assumingUserId !== undefined ||
      auth.isAssumedNotBB ||
      auth.assumerIdMlsStaff !== undefined,
    [auth],
  );

  const isBillingUser = useMemo(
    () => !userId || userId === auth.id,
    [userId, auth.id],
  );

  const canManageBilling = useMemo<CanManageBillingProfileType>(() => {
    if (isAssumedUser) {
      return {
        response: false,
        message: 'You cannot manage billing details while assuming a user.',
      };
    }
    if (!isBillingUser) {
      return {
        response: false,
        message: 'Only the owner of this account may update payment method.',
      };
    }
    return {
      response: true,
    };
  }, [isAssumedUser, isBillingUser]);

  return (
    <StyledPageWrapper>
      <StyledHeader>
        <h1>Subscriptions and Payments</h1>
      </StyledHeader>
      <StyledCardsContainer>
        <StyledCardRow>
          <PaymentCard
            canManageBilling={canManageBilling}
            viewingLevel={viewingLevel}
          />
        </StyledCardRow>
        <StyledCardRow>
          <AllSubscriptionsCard
            userId={auth.id}
            viewingLevel={viewingLevel}
            onOrgPlanClick={() => console.log('Placeholder function here')}
            onAnsweringServiceClick={() =>
              history.push(`/answering_service/${viewingLevel}`)
            }
            onSupraOneClick={() => history.push(`/profile`)}
          />
        </StyledCardRow>
        <StyledCardRow>
          <ContactDetailsCard canManageBilling={canManageBilling} />
        </StyledCardRow>
        <StyledCardRow>
          <InvoicesCard />
        </StyledCardRow>
        <StyledCardRow>
          <DocumentsCard />
        </StyledCardRow>
      </StyledCardsContainer>
    </StyledPageWrapper>
  );
};

/**
 * The page in which a user or a brokerage can manage all of their subscriptions
 * and payment methods as well as view all billing documents and invoices.
 */
const SubscriptionsPaymentsPage: React.FC = () => {
  // The viewing level must be sent in via the URL params, otherwise default to user.
  const { viewingLevel = 'user' } = useParams<{
    viewingLevel: ViewingLevel;
  }>();
  const { auth } = useContext(AuthContext);

  // If we can't get their brokerage details, render nothing.
  if (!auth?.org?.id) {
    return null;
  }

  return (
    <SubscriptionsPageProvider
      billingCustomerNumber={auth.billingCustomerNumber}
      orgId={auth.org.id}
      viewingLevel={viewingLevel}
    >
      {/** TODO remove BillingInvoicesPageContext from SubscriptionsPaymentsPage.
      Ideally, we want to move whatever we use in BillingInvoicesPageContext into SubscriptionsPageContext */}
      <BillingInvoicesPageContextProvider
        orgId={auth.org.id}
        billingCustomerNumber={auth.billingCustomerNumber}
      >
        <SubscriptionsPaymentsPageContent />
      </BillingInvoicesPageContextProvider>
    </SubscriptionsPageProvider>
  );
};

export default SubscriptionsPaymentsPage;

const StyledPageWrapper = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const StyledHeader = styled.div`
  margin-bottom: 24px;
  h1 {
    margin: 0;
    font-size: 24px;
    font-weight: 500;
    color: ${Color.BLACK};
  }
`;

const StyledCardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const StyledCardRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  &:has(> :only-child) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;
